import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Flex,
  HStack,
  Input,
  InputGroup,
  Text,
  VStack,
  chakra,
} from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import imageCompression from "browser-image-compression";
import { useReq } from "@larner.dev/use-req";
import { ReqMethod } from "@larner.dev/req";
import {
  ContactPreferenceEnum,
  GenderPreference,
  MAX_MEDIA_BYTES,
  MediaKey,
  ProfilePictureKey,
  UserWithMediaAndGroups,
} from "@greenflagdate/shared";
import { Icon, type IconName } from "../Icon/Icon";
import { BirthDateInput, validateBirthDate } from "../BirthDateInput/BirthDateInput";
import { GenderSelector } from "../GenderSelector/GenderSelector";
import { apiReq } from "../../lib/apiReq";
import { getRuntimeApiUrl } from "../../lib/runtimeApiUrl";
import { refreshUserState, store, useStoreData } from "../../lib/store/store";
import { ActionType } from "../../lib/store/storeActions";
import { toaster } from "../../lib/toaster";
import { PictureEditor } from "../../pages/marketing/Settings/PictureEditor";
import { PictureData } from "../../pages/marketing/ProfileEditor/pictureMeta";
import { VerifyPhoneNumberDialog } from "../../components/VerifyPhoneNumberDialog/VerifyPhoneNumberDialog";
import headshotAvatar from "../../pages/marketing/ProfileEditor/images/headshot_avatar.png";
import bodyAvatar from "../../pages/marketing/ProfileEditor/images/body_avatar.png";
import actionAvatar from "../../pages/marketing/ProfileEditor/images/action_avatar.png";
import friendsAvatar from "../../pages/marketing/ProfileEditor/images/friends_avatar.png";
import travelAvatar from "../../pages/marketing/ProfileEditor/images/travel_avatar.png";
import petAvatar from "../../pages/marketing/ProfileEditor/images/pet_avatar.png";

const MChakraDiv = motion.create(chakra.div);

type StepKey =
  | "photo"
  | "name"
  | "birthDate"
  | "gender"
  | "genderPreference"
  | "contact";

interface StepDef {
  key: StepKey;
  title: string;
  blurb: string;
}

const STEP_DEFS: StepDef[] = [
  {
    key: "photo",
    title: "Add a photo",
    blurb: "Matches want to see who they're meeting. One photo is all we need.",
  },
  {
    key: "name",
    title: "What's your first name?",
    blurb: "This is how you'll show up to your matches.",
  },
  {
    key: "birthDate",
    title: "When's your birthday?",
    blurb: "We use this to confirm you're 18+ and for age-based matching.",
  },
  {
    key: "gender",
    title: "What's your gender?",
    blurb: "This helps us match you with people you're compatible with.",
  },
  {
    key: "genderPreference",
    title: "Who are you looking for?",
    blurb: "We'll only show you people who match this.",
  },
  {
    key: "contact",
    title: "How should matches reach you?",
    blurb:
      "Pick at least one way. Matches will only get the channels you turn on.",
  },
];

const PHOTO_TILES: {
  key: ProfilePictureKey;
  label: string;
  avatar: string;
}[] = [
  {
    key: ProfilePictureKey.Headshot,
    label: "Headshot",
    avatar: headshotAvatar,
  },
  { key: ProfilePictureKey.Body, label: "Head to toe", avatar: bodyAvatar },
  { key: ProfilePictureKey.Action, label: "Hobby", avatar: actionAvatar },
  { key: ProfilePictureKey.Friends, label: "Friends", avatar: friendsAvatar },
  { key: ProfilePictureKey.Travel, label: "Location", avatar: travelAvatar },
  { key: ProfilePictureKey.Pet, label: "Pet / other", avatar: petAvatar },
];

function computeMissingSteps(
  user: UserWithMediaAndGroups | undefined,
): StepKey[] {
  if (!user) return STEP_DEFS.map((s) => s.key);

  const missing: StepKey[] = [];

  const mediaKeys = user.media ? Object.keys(user.media) : [];
  if (mediaKeys.length === 0) missing.push("photo");
  if (!user.first_name) missing.push("name");
  if (!user.birth_date) missing.push("birthDate");
  if (!user.gender) missing.push("gender");
  // Friends Only answers this: post-event matching includes marking people as
  // friends, so a dating question must not stand between a friends-only user
  // and their Friends tab.
  if (!user.gender_preference && !user.friends_only)
    missing.push("genderPreference");

  const prefs = Array.isArray(user.contact_preferences)
    ? user.contact_preferences
    : [];
  const hasValidContact = prefs.some((p) => {
    if (p === ContactPreferenceEnum.Email) return !!user.email;
    if (p === ContactPreferenceEnum.Phone) return !!user.phone_number;
    if (p === ContactPreferenceEnum.Instagram) return !!user.instagram_handle;
    if (p === ContactPreferenceEnum.Groupme) return true;
    return false;
  });
  if (!hasValidContact) missing.push("contact");

  return missing;
}

export function isProfileCompleteForMatching(
  user: UserWithMediaAndGroups | undefined,
): boolean {
  return computeMissingSteps(user).length === 0;
}

// Mirrors the gate's own filtering so callers can predict whether
// CompleteProfileGate will render without duplicating the logic.
export function getMissingProfileSteps(
  user: UserWithMediaAndGroups | undefined,
  requiredSteps?: StepKey[],
): StepKey[] {
  const missing = computeMissingSteps(user);
  return requiredSteps
    ? missing.filter((k) => requiredSteps.includes(k))
    : missing;
}

function ProgressDots({
  total,
  currentIndex,
  completedCount,
}: {
  total: number;
  currentIndex: number;
  completedCount: number;
}) {
  return (
    <HStack gap="6px" justify="center">
      {Array.from({ length: total }).map((_, i) => {
        const done = i < completedCount;
        const active = i === currentIndex;
        return (
          <Box
            key={i}
            h="6px"
            borderRadius="pill"
            bg={done ? "bg.brand" : active ? "bg.brand" : "border.subtle"}
            w={active ? "22px" : "6px"}
            transition="all 200ms ease"
          />
        );
      })}
    </HStack>
  );
}

function StepShell({
  title,
  blurb,
  children,
  footer,
}: {
  title: string;
  blurb: string;
  children: React.ReactNode;
  footer: React.ReactNode;
}) {
  return (
    <Flex direction="column" flex={1} minH={0}>
      <Box
        flex={1}
        minH={0}
        overflowY="auto"
        px="4"
        pt="18px"
        pb="16px"
        w="100%"
      >
        <Box maxW="30rem" mx="auto">
          <Text
            fontSize="22px"
            fontWeight="semibold"
            color="fg.default"
            lineHeight="1.25"
            mb="6px"
          >
            {title}
          </Text>
          <Text fontSize="13px" color="fg.muted" lineHeight="1.5" mb="20px">
            {blurb}
          </Text>
          {children}
        </Box>
      </Box>
      <Box
        flexShrink={0}
        px="4"
        pt="12px"
        pb="calc(12px + env(safe-area-inset-bottom))"
        bg="bg.canvas"
        borderTop="1px solid"
        borderColor="border.subtle"
        boxShadow="0 -4px 16px rgba(0,0,0,0.04)"
      >
        <Box maxW="30rem" mx="auto">
          {footer}
        </Box>
      </Box>
    </Flex>
  );
}

function PrimaryButton({
  onClick,
  disabled,
  loading,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  children: React.ReactNode;
}) {
  const off = disabled || loading;
  return (
    <chakra.button
      type="button"
      onClick={onClick}
      disabled={off}
      w="100%"
      bg={off ? "bg.subtle" : "bg.brand"}
      color={off ? "fg.muted" : "fg.onBrand"}
      border="0"
      borderRadius="pill"
      h="48px"
      px="6"
      fontWeight="bold"
      fontSize="15px"
      cursor={off ? "not-allowed" : "pointer"}
      boxShadow={off ? "none" : "0 4px 12px rgba(57,184,125,0.3)"}
      _hover={!off ? { filter: "brightness(1.04)" } : undefined}
      transition="filter 120ms ease, background 120ms ease"
    >
      {loading ? "Saving…" : children}
    </chakra.button>
  );
}

function FieldInputWrap({ children }: { children: React.ReactNode }) {
  return (
    <Flex
      align="center"
      gap="2"
      p="12px 14px"
      border="1px solid"
      borderColor="border.default"
      borderRadius="xs"
      bg="bg.canvas"
      _focusWithin={{
        borderColor: "border.brand",
        boxShadow: "0 0 0 3px rgba(57,184,125,0.25)",
      }}
    >
      {children}
    </Flex>
  );
}

function NameStep({
  onSaved,
}: {
  onSaved: (user: UserWithMediaAndGroups) => void;
}) {
  const [req, reqState] = useReq<UserWithMediaAndGroups>(apiReq);
  const [draft, setDraft] = useState("");
  const trimmed = draft.trim();
  const save = useCallback(async () => {
    if (!trimmed) return;
    const response = await req.put("/user/attributes", { first_name: trimmed });
    if (response.success) {
      store.dispatch({
        type: ActionType.UpdateUser,
        params: { user: response.result },
      });
      onSaved(response.result);
    } else {
      toaster.create({
        type: "error",
        title: "Couldn't save your name. Please try again.",
      });
    }
  }, [req, trimmed, onSaved]);

  const def = STEP_DEFS.find((s) => s.key === "name")!;
  return (
    <StepShell
      title={def.title}
      blurb={def.blurb}
      footer={
        <PrimaryButton
          onClick={save}
          disabled={!trimmed}
          loading={reqState.loading}
        >
          Continue
        </PrimaryButton>
      }
    >
      <FieldInputWrap>
        <chakra.input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="What should we call you?"
          autoFocus
          flex={1}
          fontSize="15px"
          bg="transparent"
          border="0"
          outline="none"
          color="fg.default"
          _placeholder={{ color: "fg.muted", opacity: 0.65 }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && trimmed) {
              e.preventDefault();
              void save();
            }
          }}
        />
      </FieldInputWrap>
      <Text fontSize="11px" color="fg.muted" mt="8px">
        Your first name can't be changed after you save it.
      </Text>
    </StepShell>
  );
}

function BirthDateStep({
  onSaved,
}: {
  onSaved: (user: UserWithMediaAndGroups) => void;
}) {
  const [req, reqState] = useReq<UserWithMediaAndGroups>(apiReq);
  const [draft, setDraft] = useState("");
  const { valid, tooYoung } = validateBirthDate(draft);
  const save = useCallback(async () => {
    if (!valid) {
      toaster.create({
        type: "error",
        title: "That birth date doesn't look right.",
      });
      return;
    }
    if (tooYoung) {
      toaster.create({
        type: "error",
        title: "You must be at least 18 to use Green Flag.",
      });
      return;
    }
    const response = await req.put("/user/attributes", {
      birth_date: dayjs(draft).format("YYYY-MM-DD"),
    });
    if (response.success) {
      store.dispatch({
        type: ActionType.UpdateUser,
        params: { user: response.result },
      });
      onSaved(response.result);
    } else {
      toaster.create({
        type: "error",
        title: "Couldn't save your birth date. Please try again.",
      });
    }
  }, [req, draft, valid, tooYoung, onSaved]);

  const def = STEP_DEFS.find((s) => s.key === "birthDate")!;
  return (
    <StepShell
      title={def.title}
      blurb={def.blurb}
      footer={
        <PrimaryButton
          onClick={save}
          disabled={!valid || tooYoung}
          loading={reqState.loading}
        >
          Continue
        </PrimaryButton>
      }
    >
      <BirthDateInput value={draft} onChange={setDraft} />
      {tooYoung && (
        <Text fontSize="12px" color="fg.danger" mt="8px">
          You must be 18 or older to use Green Flag.
        </Text>
      )}
      <Text fontSize="11px" color="fg.muted" mt="8px">
        Your birth date can't be changed after you save it.
      </Text>
    </StepShell>
  );
}

function GenderOption({
  active,
  onClick,
  disabled,
  children,
}: {
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <chakra.button
      type="button"
      onClick={onClick}
      disabled={disabled}
      display="flex"
      alignItems="center"
      gap="3"
      w="100%"
      p="13px 14px"
      border="1px solid"
      borderColor={active ? "border.brand" : "border.default"}
      bg={active ? "bg.brandSoft" : "bg.canvas"}
      borderRadius="pill"
      cursor={disabled ? "not-allowed" : "pointer"}
      textAlign="left"
      transition="all 120ms ease"
      _hover={!active && !disabled ? { bg: "#FAFBFB" } : undefined}
    >
      <Flex
        w="20px"
        h="20px"
        borderRadius="full"
        border="1.5px solid"
        borderColor={active ? "border.brand" : "border.default"}
        bg={active ? "bg.brand" : "transparent"}
        align="center"
        justify="center"
        flexShrink={0}
      >
        {active && <Icon name="check" boxSize="10px" color="fg.onBrand" />}
      </Flex>
      <Text
        as="span"
        fontSize="14px"
        fontWeight={active ? "semibold" : "medium"}
        color={active ? "fg.onTint" : "fg.default"}
      >
        {children}
      </Text>
    </chakra.button>
  );
}

function GenderStep({
  onSaved,
}: {
  onSaved: (user: UserWithMediaAndGroups) => void;
}) {
  const [req, reqState] = useReq<UserWithMediaAndGroups>(apiReq);
  const [selected, setSelected] = useState<string | undefined>();
  const gender = selected?.trim();
  const save = useCallback(async () => {
    if (!gender) return;
    const response = await req.put("/user/attributes", { gender });
    if (response.success) {
      store.dispatch({
        type: ActionType.UpdateUser,
        params: { user: response.result },
      });
      onSaved(response.result);
    } else {
      toaster.create({
        type: "error",
        title: "Couldn't save your gender. Please try again.",
      });
    }
  }, [req, gender, onSaved]);

  const def = STEP_DEFS.find((s) => s.key === "gender")!;
  return (
    <StepShell
      title={def.title}
      blurb={def.blurb}
      footer={
        <PrimaryButton
          onClick={save}
          disabled={!gender}
          loading={reqState.loading}
        >
          Continue
        </PrimaryButton>
      }
    >
      <GenderSelector value={selected} onChange={setSelected} />
    </StepShell>
  );
}

function GenderPreferenceStep({
  onSaved,
}: {
  onSaved: (user: UserWithMediaAndGroups) => void;
}) {
  const [req, reqState] = useReq<UserWithMediaAndGroups>(apiReq);
  const [selected, setSelected] = useState<GenderPreference | undefined>();
  const [friendsOnly, setFriendsOnly] = useState(false);
  const save = useCallback(async () => {
    if (!selected && !friendsOnly) return;
    const response = await req.put(
      "/user/attributes",
      friendsOnly
        ? { friends_only: true }
        : { gender_preference: selected, friends_only: false }
    );
    if (response.success) {
      store.dispatch({
        type: ActionType.UpdateUser,
        params: { user: response.result },
      });
      // Both branches invalidate the cached past-spark categories — they are
      // derived server-side from friends_only AND gender_preference. This gate
      // also runs on the match page, where the screen behind it is the very
      // thing reading them.
      await refreshUserState();
      onSaved(response.result);
    } else {
      toaster.create({
        type: "error",
        title: "Couldn't save your preference. Please try again.",
      });
    }
  }, [req, selected, friendsOnly, onSaved]);

  const options: { value: GenderPreference; label: string }[] = [
    { value: "man", label: "Men" },
    { value: "woman", label: "Women" },
    { value: "everyone", label: "Everyone" },
  ];

  const def = STEP_DEFS.find((s) => s.key === "genderPreference")!;
  return (
    <StepShell
      title={def.title}
      blurb={def.blurb}
      footer={
        <PrimaryButton
          onClick={save}
          disabled={!selected && !friendsOnly}
          loading={reqState.loading}
        >
          Continue
        </PrimaryButton>
      }
    >
      <VStack align="stretch" gap="10px" opacity={friendsOnly ? 0.45 : 1}>
        {options.map((opt) => (
          <GenderOption
            key={opt.value}
            active={!friendsOnly && selected === opt.value}
            disabled={friendsOnly}
            onClick={() => {
              setSelected(opt.value);
              setFriendsOnly(false);
            }}
          >
            {opt.label}
          </GenderOption>
        ))}
      </VStack>

      {/* The way out of the dating question for someone who isn't dating.
          Same option sign-up and event registration offer, so switching to
          friends mode is available wherever we'd otherwise insist on a
          dating preference. */}
      <chakra.button
        type="button"
        onClick={() => setFriendsOnly((on) => !on)}
        display="flex"
        alignItems="flex-start"
        gap="3"
        w="100%"
        mt="14px"
        p="13px 14px"
        border="1px solid"
        borderColor={friendsOnly ? "border.friends" : "border.default"}
        bg={friendsOnly ? "bg.friendsSoft" : "bg.canvas"}
        borderRadius="14px"
        cursor="pointer"
        textAlign="left"
        transition="all 120ms ease"
        _hover={!friendsOnly ? { bg: "#FAFBFB" } : undefined}
      >
        <Flex
          mt="1px"
          w="20px"
          h="20px"
          borderRadius="6px"
          border="1.5px solid"
          borderColor={friendsOnly ? "border.friends" : "border.default"}
          bg={friendsOnly ? "bg.friends" : "transparent"}
          align="center"
          justify="center"
          flexShrink={0}
        >
          {friendsOnly && <Icon name="check" boxSize="10px" color="fg.onBrand" />}
        </Flex>
        <Box>
          <Text as="span" display="block" fontSize="14px" fontWeight="semibold" color="fg.default">
            I'm here for friends, not dating
          </Text>
          <Text fontSize="12px" color="fg.muted" lineHeight="1.4" mt="2px">
            Your profile won't show up for dating. You'll still match as
            friends after events you attend, and you can change this any time.
          </Text>
        </Box>
      </chakra.button>
    </StepShell>
  );
}

function PhotoStep({
  onSaved,
  token,
}: {
  onSaved: () => void;
  token?: string;
}) {
  const [mediaReq] = useReq<UserWithMediaAndGroups>(apiReq);
  const storeData = useStoreData();
  const user = storeData.user;
  const [activePictureType, setActivePictureType] = useState<
    ProfilePictureKey | undefined
  >();
  const [pictures, setPictures] = useState<
    Partial<Record<ProfilePictureKey, PictureData>>
  >({});

  useEffect(() => {
    const media = user?.media;
    if (!media) return;
    const keys = Object.keys(media);
    if (!keys.length) return;
    let cancelled = false;
    (async () => {
      const blobs = await Promise.all(
        keys.map(async (key) => {
          const entry = media[key as unknown as MediaKey];
          if (!entry) return null;
          const url = new URL(entry.urls.original, getRuntimeApiUrl());
          try {
            const res = await fetch(url, {
              headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            });
            if (!res.ok) return null;
            const blob = await res.blob();
            return {
              key,
              data: {
                blob,
                imageSrc: URL.createObjectURL(blob),
                cropArea: {
                  width: entry.crop_width_percent,
                  height: entry.crop_height_percent,
                  x: entry.crop_x,
                  y: entry.crop_y,
                },
              } as PictureData,
            };
          } catch {
            return null;
          }
        }),
      );
      if (cancelled) return;
      const next: Partial<Record<ProfilePictureKey, PictureData>> = {};
      for (const entry of blobs) {
        if (entry) next[entry.key as ProfilePictureKey] = entry.data;
      }
      setPictures((prev) => ({ ...next, ...prev }));
    })();
    return () => {
      cancelled = true;
    };
  }, [token, user?.media]);

  const hasAny = Object.keys(pictures).length > 0;

  const def = STEP_DEFS.find((s) => s.key === "photo")!;
  return (
    <>
      <StepShell
        title={def.title}
        blurb={def.blurb}
        footer={
          <PrimaryButton onClick={onSaved} disabled={!hasAny}>
            Continue
          </PrimaryButton>
        }
      >
        <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap="10px">
          {PHOTO_TILES.map((t, i) => {
            const pic = pictures[t.key];
            const empty = !pic;
            return (
              <Flex key={t.key} direction="column" gap="6px">
                <chakra.button
                  type="button"
                  onClick={() => setActivePictureType(t.key)}
                  position="relative"
                  aspectRatio="1 / 1"
                  borderRadius="xs"
                  overflow="hidden"
                  bg={empty ? "bg.subtle" : "#eee"}
                  border={empty ? "1.5px dashed" : "0"}
                  borderColor="border.default"
                  boxShadow={empty ? "none" : "0 2px 2px rgba(0,0,0,0.10)"}
                  cursor="pointer"
                  p="0"
                  w="100%"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  _hover={{ filter: "brightness(1.02)" }}
                >
                  {empty ? (
                    <Flex
                      w="32px"
                      h="32px"
                      borderRadius="full"
                      bg="bg.brandSoft"
                      color="fg.brand"
                      align="center"
                      justify="center"
                    >
                      <Icon name="plus" boxSize="12px" />
                    </Flex>
                  ) : (
                    <>
                      <CroppedImage
                        src={pic!.imageSrc}
                        cropArea={pic!.cropArea}
                        alt={t.label}
                      />
                      {i === 0 && (
                        <Box
                          position="absolute"
                          top="6px"
                          left="6px"
                          bg="bg.brand"
                          color="fg.onBrand"
                          fontSize="8px"
                          fontWeight="bold"
                          letterSpacing="0.08em"
                          textTransform="uppercase"
                          px="7px"
                          py="3px"
                          borderRadius="pill"
                        >
                          Main
                        </Box>
                      )}
                      <Flex
                        position="absolute"
                        bottom="6px"
                        right="6px"
                        w="22px"
                        h="22px"
                        borderRadius="full"
                        bg="rgba(0,0,0,0.55)"
                        color="white"
                        align="center"
                        justify="center"
                      >
                        <Icon name="camera" boxSize="10px" />
                      </Flex>
                    </>
                  )}
                  {empty && (
                    <Box
                      position="absolute"
                      inset={0}
                      opacity={0.25}
                      backgroundImage={`url(${t.avatar})`}
                      backgroundSize="cover"
                      backgroundPosition="center"
                      pointerEvents="none"
                      mixBlendMode="multiply"
                    />
                  )}
                </chakra.button>
                <Text
                  fontSize="11px"
                  fontWeight="semibold"
                  color={empty ? "fg.muted" : "fg.default"}
                  textAlign="center"
                >
                  {t.label}
                </Text>
              </Flex>
            );
          })}
        </Box>
      </StepShell>

      <PictureEditor
        isShown={!!activePictureType}
        pictureType={activePictureType}
        blob={activePictureType && pictures[activePictureType]?.blob}
        cropArea={activePictureType && pictures[activePictureType]?.cropArea}
        onClose={() => setActivePictureType(undefined)}
        onSave={async (data) => {
          const key = activePictureType;
          if (!key) return;
          setPictures((prev) => ({ ...prev, [key]: data }));
          setActivePictureType(undefined);

          let uploadBlob = data.blob;
          if (uploadBlob.size > MAX_MEDIA_BYTES) {
            uploadBlob = await imageCompression(uploadBlob as File, {
              maxSizeMB: MAX_MEDIA_BYTES / 1024 / 1024,
              useWebWorker: true,
            });
          }
          const formData = new FormData();
          formData.append("key", key);
          formData.append(
            "cropWidthPercentage",
            data.cropArea.width.toString(),
          );
          formData.append(
            "cropHeightPercentage",
            data.cropArea.height.toString(),
          );
          formData.append("cropX", data.cropArea.x.toString());
          formData.append("cropY", data.cropArea.y.toString());
          formData.append("media", uploadBlob, key);
          const response = await mediaReq[ReqMethod.PUT]("/media", formData);
          if (response.success) {
            store.dispatch({
              type: ActionType.UpdateUser,
              params: { user: response.result },
            });
          } else {
            setPictures((prev) => {
              const copy = { ...prev };
              delete copy[key];
              return copy;
            });
            toaster.create({
              type: "error",
              title:
                "Something went wrong uploading your photo. Please try again.",
            });
          }
        }}
        onDelete={async () => {
          const key = activePictureType;
          if (!key) return;
          const response = await mediaReq[ReqMethod.DELETE](
            `/media/${key}`,
            {},
          );
          if (response.success) {
            store.dispatch({
              type: ActionType.UpdateUser,
              params: { user: response.result },
            });
            setPictures((prev) => {
              const copy = { ...prev };
              delete copy[key];
              return copy;
            });
            setActivePictureType(undefined);
          }
        }}
      />
    </>
  );
}

function CroppedImage({
  src,
  cropArea,
  alt,
}: {
  src: string;
  cropArea: { x: number; y: number; width: number; height: number };
  alt: string;
}) {
  const scale = 100 / cropArea.width;
  return (
    <Box position="absolute" inset={0} overflow="hidden">
      <chakra.img
        src={src}
        alt={alt}
        position="absolute"
        top={0}
        left={0}
        display="block"
        w="calc(100% + 0.5px)"
        h="auto"
        style={{
          transformOrigin: "top left",
          transform: `translate3d(${-cropArea.x * scale}%, ${-cropArea.y * scale}%, 0) scale3d(${scale}, ${scale}, 1)`,
        }}
      />
    </Box>
  );
}

function ContactStep({ onDone }: { onDone: () => void }) {
  const storeData = useStoreData();
  const user = storeData.user;
  const [req, reqState] = useReq<UserWithMediaAndGroups>(apiReq);
  const [showVerifyPhone, setShowVerifyPhone] = useState(false);

  const rawPrefs = user?.contact_preferences;
  const serverPrefs = useMemo(
    () => (Array.isArray(rawPrefs) ? rawPrefs : []),
    [rawPrefs],
  );

  // Local drafts — the step doesn't persist anything until the user clicks
  // the footer button. Phone verification is the one exception: it has its
  // own server-side dialog that sets `user.phone_number`, which we detect
  // and auto-enable the Phone preference in the local draft.
  const [selected, setSelected] = useState<Set<ContactPreferenceEnum>>(
    () => new Set(serverPrefs),
  );
  const [instagramDraft, setInstagramDraft] = useState(
    user?.instagram_handle ?? "",
  );
  const instagramTrimmed = instagramDraft.trim();

  const toggle = useCallback((pref: ContactPreferenceEnum) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(pref)) next.delete(pref);
      else next.add(pref);
      return next;
    });
  }, []);

  const has = useCallback(
    (p: ContactPreferenceEnum) => selected.has(p),
    [selected],
  );

  const hasValid = useMemo(() => {
    for (const p of selected) {
      if (p === ContactPreferenceEnum.Email && user?.email) return true;
      if (p === ContactPreferenceEnum.Phone && user?.phone_number) return true;
      if (p === ContactPreferenceEnum.Instagram && instagramTrimmed)
        return true;
      if (p === ContactPreferenceEnum.Groupme) return true;
    }
    return false;
  }, [selected, user?.email, user?.phone_number, instagramTrimmed]);

  const submit = useCallback(async () => {
    const payload: {
      contact_preferences: ContactPreferenceEnum[];
      instagram_handle?: string;
    } = {
      contact_preferences: Array.from(selected),
    };
    if (selected.has(ContactPreferenceEnum.Instagram)) {
      payload.instagram_handle = instagramTrimmed || undefined;
    }
    const response = await req.put("/user/attributes", payload);
    if (response.success) {
      store.dispatch({
        type: ActionType.UpdateUser,
        params: { user: response.result },
      });
      onDone();
    } else {
      toaster.create({
        type: "error",
        title: "Couldn't save your contact preferences.",
      });
    }
  }, [req, selected, instagramTrimmed, onDone]);

  const def = STEP_DEFS.find((s) => s.key === "contact")!;
  return (
    <>
      <StepShell
        title={def.title}
        blurb={def.blurb}
        footer={
          <PrimaryButton
            onClick={submit}
            disabled={!hasValid}
            loading={reqState.loading}
          >
            Start Matching
          </PrimaryButton>
        }
      >
        <Box
          bg="bg.canvas"
          border="1px solid"
          borderColor="border.subtle"
          borderRadius="sm"
          boxShadow="card"
          overflow="hidden"
        >
          <ContactRow
            isFirst
            icon="envelope"
            title="Email"
            detail={
              user?.email || (
                <Text as="span" color="fg.muted">
                  No email on file
                </Text>
              )
            }
            checked={has(ContactPreferenceEnum.Email)}
            disabled={!user?.email}
            onToggle={() => toggle(ContactPreferenceEnum.Email)}
          />
          <ContactRow
            icon="user"
            title="Phone number"
            detail={
              user?.phone_number ||
              user?.phone_number_to_validate || (
                <Text as="span" color="fg.muted">
                  Add a phone number to share by text
                </Text>
              )
            }
            checked={has(ContactPreferenceEnum.Phone)}
            onToggle={() => {
              if (user?.phone_number) {
                toggle(ContactPreferenceEnum.Phone);
              } else {
                setShowVerifyPhone(true);
              }
            }}
          />
          <ContactRow
            icon="heart"
            title="Instagram"
            checked={has(ContactPreferenceEnum.Instagram)}
            onToggle={() => toggle(ContactPreferenceEnum.Instagram)}
            action={
              has(ContactPreferenceEnum.Instagram) ? (
                <InputGroup
                  startElement={
                    <Text as="span" fontSize="13px" color="fg.muted">
                      @
                    </Text>
                  }
                >
                  <Input
                    type="text"
                    value={instagramDraft}
                    onChange={(e) => setInstagramDraft(e.target.value)}
                    placeholder="greenflagdate"
                    fontSize="13px"
                  />
                </InputGroup>
              ) : undefined
            }
          />
        </Box>
        {!hasValid && (
          <Text fontSize="12px" color="fg.muted" mt="12px">
            Turn on at least one channel — and make sure it has a value — to
            continue.
          </Text>
        )}
      </StepShell>

      <VerifyPhoneNumberDialog
        isShown={showVerifyPhone}
        offerTextOptIn
        onClose={() => setShowVerifyPhone(false)}
        onSuccess={() =>
          setSelected((prev) => {
            const next = new Set(prev);
            next.add(ContactPreferenceEnum.Phone);
            return next;
          })
        }
      />
    </>
  );
}

function ContactRow({
  icon,
  title,
  detail,
  action,
  checked,
  onToggle,
  disabled,
  isFirst,
}: {
  icon: IconName;
  title: string;
  detail?: React.ReactNode;
  action?: React.ReactNode;
  checked: boolean;
  onToggle: () => void;
  disabled?: boolean;
  isFirst?: boolean;
}) {
  return (
    <Flex
      align="center"
      gap="14px"
      p="14px 16px"
      borderTop={isFirst ? "0" : "1px solid"}
      borderTopColor="border.subtle"
    >
      <Flex
        w="36px"
        h="36px"
        borderRadius="full"
        align="center"
        justify="center"
        flexShrink={0}
        bg="rgba(11,128,205,0.12)"
        color="bg.friends"
      >
        <Icon name={icon} boxSize="16px" />
      </Flex>
      <Box flex={1} minW="0">
        <Text
          fontSize="14px"
          fontWeight="semibold"
          color="fg.default"
          lineHeight="1.25"
        >
          {title}
        </Text>
        {detail && (
          <Box mt="2px" fontSize="12px" color="fg.muted" lineHeight="1.35">
            {detail}
          </Box>
        )}
        {action && <Box mt="8px">{action}</Box>}
      </Box>
      <chakra.button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={`Share ${title.toLowerCase()}`}
        onClick={() => !disabled && onToggle()}
        disabled={disabled}
        position="relative"
        w="44px"
        h="26px"
        borderRadius="pill"
        bg={checked ? "bg.brand" : "#D6D9DD"}
        border="0"
        cursor={disabled ? "not-allowed" : "pointer"}
        transition="background 150ms ease"
        flexShrink={0}
        opacity={disabled ? 0.55 : 1}
        _hover={!disabled ? { filter: "brightness(1.04)" } : undefined}
      >
        <Box
          position="absolute"
          top="3px"
          left={checked ? "21px" : "3px"}
          w="20px"
          h="20px"
          borderRadius="full"
          bg="white"
          boxShadow="0 1px 3px rgba(0,0,0,0.25)"
          transition="left 150ms ease"
        />
      </chakra.button>
    </Flex>
  );
}

const slideVariants = {
  initial: { opacity: 0, x: 24 },
  animate: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.25, ease: "easeOut" as const },
  },
  exit: {
    opacity: 0,
    x: -24,
    transition: { duration: 0.2, ease: "easeIn" as const },
  },
};

const gateVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { duration: 0.15 } },
};

export interface CompleteProfileGateIntro {
  // Rendered inside the gate container before the profile steps start.
  // Should include its own primary action that calls onDismiss to advance.
  render: (onDismiss: () => void) => React.ReactNode;
}

export function CompleteProfileGate({
  requiredSteps,
  dismissible = true,
  intro,
}: {
  requiredSteps?: StepKey[];
  dismissible?: boolean;
  intro?: CompleteProfileGateIntro;
} = {}) {
  const storeData = useStoreData();
  const navigate = useNavigate();
  const user = storeData.user;
  const token = storeData.auth?.token;

  const handleClose = useCallback(() => {
    navigate("/events");
  }, [navigate]);

  // Callers often pass `requiredSteps` as an inline array literal, so its
  // reference changes every render. Key effects off the content instead.
  const requiredStepsKey = requiredSteps ? requiredSteps.join(",") : "";

  // Snapshot the list of steps once, when the gate first opens. We do NOT
  // recompute this as the user fills fields in — otherwise completing a step
  // would shrink the list and the user would be silently auto-advanced to
  // the next step by the render (the same stepIndex now pointing at a
  // different element). Advance happens only when the user clicks Continue.
  const [plan, setPlan] = useState<StepKey[] | null>(null);
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    if (plan !== null) return;
    if (!user) return;
    const allMissing = computeMissingSteps(user);
    const missing = requiredSteps
      ? allMissing.filter((k) => requiredSteps.includes(k))
      : allMissing;
    if (missing.length > 0) {
      setPlan(STEP_DEFS.filter((s) => missing.includes(s.key)).map((s) => s.key));
      setStepIndex(0);
    } else {
      setPlan([]);
    }
    // requiredSteps identity intentionally excluded — requiredStepsKey tracks content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, user, requiredStepsKey]);

  const orderedSteps = useMemo(() => {
    if (!plan) return [] as StepDef[];
    return plan
      .map((key) => STEP_DEFS.find((s) => s.key === key))
      .filter((s): s is StepDef => !!s);
  }, [plan]);

  const advance = useCallback(() => {
    setStepIndex((i) => i + 1);
  }, []);

  // The gate stays mounted until the user has walked past the last step AND
  // the live profile is actually complete. If they get to the end with
  // something still missing, we re-open with a fresh plan covering the gap.
  useEffect(() => {
    if (!plan) return;
    if (stepIndex < plan.length) return;
    const allMissing = computeMissingSteps(user);
    const missing = requiredSteps
      ? allMissing.filter((k) => requiredSteps.includes(k))
      : allMissing;
    if (missing.length === 0) {
      if (plan.length !== 0) setPlan([]);
    } else {
      setPlan(STEP_DEFS.filter((s) => missing.includes(s.key)).map((s) => s.key));
      setStepIndex(0);
    }
    // requiredSteps identity intentionally excluded — requiredStepsKey tracks content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex, plan, user, requiredStepsKey]);

  // The intro panel (when provided) sits in front of the profile steps. It
  // is dismissed by the consumer's render callback, which flips this flag
  // and reveals the regular gate header + step body underneath.
  const [introDismissed, setIntroDismissed] = useState(false);
  const showIntro = !!intro && !introDismissed;

  if (!plan || plan.length === 0) return null;
  const currentStep = orderedSteps[stepIndex];
  if (!currentStep) return null;

  const totalInitial = Math.max(orderedSteps.length, 1);
  const completedCount = stepIndex;

  let stepBody: React.ReactNode = null;
  switch (currentStep.key) {
    case "photo":
      stepBody = <PhotoStep onSaved={advance} token={token} />;
      break;
    case "name":
      stepBody = <NameStep onSaved={advance} />;
      break;
    case "birthDate":
      stepBody = <BirthDateStep onSaved={advance} />;
      break;
    case "gender":
      stepBody = <GenderStep onSaved={advance} />;
      break;
    case "genderPreference":
      stepBody = <GenderPreferenceStep onSaved={advance} />;
      break;
    case "contact":
      stepBody = <ContactStep onDone={advance} />;
      break;
  }

  return (
    <AnimatePresence>
      <MChakraDiv
        key="complete-profile-gate"
        initial="initial"
        animate="animate"
        exit="exit"
        variants={gateVariants}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 50,
          background: "rgba(15, 23, 30, 0.55)",
          backdropFilter: "blur(2px)",
          display: "flex",
          alignItems: "stretch",
          justifyContent: "center",
          paddingTop: "calc(env(safe-area-inset-top) + 24px)",
          paddingBottom: "calc(env(safe-area-inset-bottom) + 24px)",
          paddingLeft: "calc(env(safe-area-inset-left) + 16px)",
          paddingRight: "calc(env(safe-area-inset-right) + 16px)",
        }}
      >
        <Box
          w="100%"
          maxW="32rem"
          bg="bg.canvas"
          borderRadius="sm"
          overflow="hidden"
          boxShadow="0 24px 60px rgba(0,0,0,0.35)"
          display="flex"
          flexDirection="column"
          minH={0}
        >
          {showIntro ? (
            <AnimatePresence mode="wait" initial={false}>
              <MChakraDiv
                key="intro"
                initial="initial"
                animate="animate"
                exit="exit"
                variants={slideVariants}
                style={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {intro!.render(() => setIntroDismissed(true))}
              </MChakraDiv>
            </AnimatePresence>
          ) : (
            <>
              <Box
                flexShrink={0}
                px="4"
                pt="14px"
                pb="14px"
                borderBottom="1px solid"
                borderColor="border.subtle"
              >
                <Flex align="center" gap="3" mb="10px">
                  <Text
                    flex={1}
                    fontSize="10px"
                    letterSpacing="0.14em"
                    textTransform="uppercase"
                    color="fg.muted"
                    fontWeight="semibold"
                  >
                    Finish your profile before matching
                  </Text>
                  <Text fontSize="11px" color="fg.muted" fontWeight="semibold">
                    {Math.min(stepIndex + 1, totalInitial)} of {totalInitial}
                  </Text>
                  {dismissible && (
                    <chakra.button
                      type="button"
                      onClick={handleClose}
                      aria-label="Close and go back"
                      w="28px"
                      h="28px"
                      borderRadius="full"
                      bg="bg.subtle"
                      color="fg.muted"
                      border="0"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      cursor="pointer"
                      flexShrink={0}
                      _hover={{ bg: "#E8EBED", color: "fg.default" }}
                    >
                      <Icon name="x" boxSize="11px" />
                    </chakra.button>
                  )}
                </Flex>
                <ProgressDots
                  total={totalInitial}
                  currentIndex={stepIndex}
                  completedCount={completedCount}
                />
              </Box>

              <AnimatePresence mode="wait" initial={false}>
                <MChakraDiv
                  key={currentStep.key}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  variants={slideVariants}
                  style={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {stepBody}
                </MChakraDiv>
              </AnimatePresence>
            </>
          )}
        </Box>
      </MChakraDiv>
    </AnimatePresence>
  );
}
